// LT-511: public booking page (DS v1 redesign). Replaces the
// hand-rolled-tailwind version. Two-pane layout, cyan accent, 24h time,
// tabular-nums. Phase machine: loading → ready → details → booking →
// success | error.
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import {
  AvailabilityResponse, BookingRequest, publicBookingAPI,
} from '../../services/publicBookingService';

import './BookingPage.css';
import { slotDate, todayInTz } from './formatters';
import TopBar from './components/TopBar';
import MeetingInfoPanel from './components/MeetingInfoPanel';
import MiniCalendar from './components/MiniCalendar';
import TimeSlotList from './components/TimeSlotList';
import BookingForm, { BookingFormValues } from './components/BookingForm';
import BookingSuccess from './components/BookingSuccess';

type Phase = 'loading' | 'ready' | 'details' | 'booking' | 'success' | 'error';

const PublicBookingPage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const [search] = useSearchParams();
  const enrollmentToken = search.get('t');

  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<AvailabilityResponse | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [committedForm, setCommittedForm] = useState<BookingFormValues | null>(null);

  const tz = data?.timezone ?? 'UTC';

  // Group slots by date in the host's timezone.
  const slotsByDate = useMemo(() => {
    if (!data) return new Map<string, AvailabilityResponse['slots']>();
    const map = new Map<string, AvailabilityResponse['slots']>();
    for (const s of data.slots) {
      const day = slotDate(s.start, data.timezone);
      const list = map.get(day) ?? [];
      list.push(s);
      map.set(day, list);
    }
    return map;
  }, [data]);

  const availableDates = useMemo(
    () => new Set(slotsByDate.keys()),
    [slotsByDate],
  );

  const today = useMemo(() => todayInTz(tz), [tz]);

  // Default the calendar view to the earliest available day (or today).
  const [viewMonth, setViewMonth] = useState<Date>(() => {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), 1);
  });

  useEffect(() => {
    if (!data) return;
    const dates = Array.from(slotsByDate.keys()).sort();
    if (dates.length > 0) {
      const [y, m] = dates[0].split('-').map(Number);
      setViewMonth(new Date(y, m - 1, 1));
    }
  }, [data, slotsByDate]);

  const fetchAvailability = async () => {
    try {
      const a = await publicBookingAPI.getAvailability(slug);
      setData(a);
      setPhase('ready');
    } catch (e: any) {
      setPhase('error');
      setError(e?.response?.status === 404
        ? 'Booking page not found.'
        : 'Unable to load booking page.');
    }
  };

  useEffect(() => {
    void fetchAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const slotsForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    return slotsByDate.get(selectedDate) ?? [];
  }, [selectedDate, slotsByDate]);

  const handleSubmit = async (values: BookingFormValues) => {
    if (!selectedSlot) return;
    setPhase('booking');
    setError(null);
    try {
      const body: BookingRequest = {
        start_time: selectedSlot,
        contact_name: values.name.trim(),
        contact_email: values.email.trim(),
        notes: values.notes.trim() || null,
        enrollment_token: enrollmentToken,
      };
      await publicBookingAPI.book(slug, body);
      setCommittedForm(values);
      setPhase('success');
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 409) {
        setError('That slot just got booked. Refreshing availability…');
        setSelectedSlot(null);
        try {
          await fetchAvailability();
        } catch {
          setPhase('error');
        }
      } else if (status === 429) {
        setError('Too many requests. Wait a minute and try again.');
        setPhase('details');
      } else if (status === 422) {
        setError(e?.response?.data?.detail?.message ?? 'That slot is no longer available.');
        setPhase('details');
      } else {
        setError('Booking failed. Please try again later.');
        setPhase('details');
      }
    }
  };

  const reset = () => {
    setSelectedDate(null);
    setSelectedSlot(null);
    setCommittedForm(null);
    setError(null);
    setPhase('ready');
    void fetchAvailability();
  };

  if (phase === 'loading') {
    return (
      <div className="min-h-screen bg-surface-bg bp-page-backdrop px-6 py-8" data-testid="public-booking-loading">
        <div className="max-w-[1080px] mx-auto text-center text-slate-400 text-sm py-20">
          Loading booking page…
        </div>
      </div>
    );
  }

  if (phase === 'error' && !data) {
    return (
      <div className="min-h-screen bg-surface-bg bp-page-backdrop px-6 py-8">
        <TopBar />
        <div
          className="max-w-[560px] mx-auto text-center text-danger-text text-sm py-20"
          data-testid="public-booking-error"
        >
          {error}
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div
      className="min-h-screen bg-surface-bg bp-page-backdrop px-6 pt-8 pb-20"
      data-testid="public-booking-page"
    >
      <TopBar />

      <div
        className="max-w-[1080px] mx-auto grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)_360px] bg-surface-1 border border-white/[0.08] rounded-ds-xl shadow-ds-e3 overflow-hidden bp-fade-up"
      >
        {/* Left: meeting info */}
        <div
          className="p-8 lg:border-r border-white/[0.08]"
          style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.01), transparent)' }}
        >
          <MeetingInfoPanel data={data} />
        </div>

        {/* Middle: calendar */}
        <div className="p-8 lg:border-r border-white/[0.08]">
          <MiniCalendar
            viewMonth={viewMonth}
            setViewMonth={setViewMonth}
            available={availableDates}
            selected={selectedDate}
            today={today}
            onSelect={(d) => {
              setSelectedDate(d);
              setSelectedSlot(null);
              if (phase !== 'ready') setPhase('ready');
            }}
          />
          <div className="mt-6 text-[11px] text-slate-500 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-accent" aria-hidden="true" />
            dates with availability
          </div>
        </div>

        {/* Right: slots / form / success */}
        <div className="p-8 bg-surface-0 flex flex-col min-h-[540px]">
          {phase === 'success' && committedForm ? (
            <BookingSuccess
              date={selectedDate!}
              slotIso={selectedSlot!}
              timezone={data.timezone}
              durationMinutes={data.duration_minutes}
              ownerName={data.owner_name}
              locationLabel={data.location_label}
              email={committedForm.email}
              onReset={reset}
            />
          ) : phase === 'details' || phase === 'booking' ? (
            <BookingForm
              date={selectedDate!}
              slotIso={selectedSlot!}
              timezone={data.timezone}
              durationMinutes={data.duration_minutes}
              ownerName={data.owner_name}
              busy={phase === 'booking'}
              inlineError={error}
              onBack={() => { setError(null); setPhase('ready'); }}
              onSubmit={handleSubmit}
            />
          ) : (
            <TimeSlotList
              date={selectedDate}
              slots={slotsForSelectedDate}
              timezone={data.timezone}
              selected={selectedSlot}
              onSelect={setSelectedSlot}
              onContinue={() => setPhase('details')}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicBookingPage;
